import React, { useEffect, useState } from "react";
import { 
  Form, Input, Button, message, Modal, Row, Col, Card, 
  Tag, Space, Divider, Alert, Tooltip, Badge 
} from "antd";
import {
  BankOutlined, IdcardOutlined, DollarOutlined,
  SaveOutlined, EditOutlined, CheckCircleOutlined,
  ExclamationCircleOutlined, EyeOutlined, EyeInvisibleOutlined,
  LockOutlined, CalendarOutlined, UserOutlined,
  SafetyOutlined, SecurityScanOutlined, InfoCircleOutlined,
  CopyOutlined, WalletOutlined,
} from "@ant-design/icons";
import { UserGet, UserUpdate } from "../../../../Api/CoreApi";
import { formatDateTime } from "../../../Admin_Panel/Utils/formatters";
import "./StaffBankDetails.css";

function StaffBankDetails() {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [hasBankDetails, setHasBankDetails] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [staffData, setStaffData] = useState(null);
  const staffId = localStorage.getItem("user_id");

  const fetchBankDetails = async () => {
    try {
      const res = await UserGet();
      const allUsers = Array.isArray(res) ? res : [];
      const staff = allUsers.find((u) => String(u.id) === String(staffId));
      
      if (staff) {
        setStaffData(staff);
        form.setFieldsValue({
          bank_account: staff.bank_account || "",
          ifsc_code: staff.ifsc_code || "",
          upi_id: staff.upi_id || "",
        });
        
        const hasDetails = !!(staff.bank_account && 
                             staff.bank_account !== 'Account Number' &&
                             staff.bank_account !== '0000000000');
        setHasBankDetails(hasDetails);
        
        if (staff.bank_updated_at) setLastUpdated(staff.bank_updated_at);
        else if (staff.updated_at) setLastUpdated(staff.updated_at);
      }
    } catch (error) {
      message.error("Failed to fetch bank details");
    }
  };

  useEffect(() => { fetchBankDetails(); }, []);

  const maskAccount = (account) => {
    if (!account || account.length < 4) return account || "N/A";
    return "●●●●●●" + account.slice(-4);
  };

  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    message.success(`${label} copied!`);
  };

  const handleSave = async (values) => {
    if (!values.bank_account || values.bank_account.length < 9) {
      message.warning("Bank account number must be at least 9 digits");
      return;
    }
    if (!values.ifsc_code || values.ifsc_code.length < 11) {
      message.warning("Please enter a valid IFSC code");
      return;
    }
    if (!values.upi_id || !values.upi_id.includes('@')) {
      message.warning("Please enter a valid UPI ID");
      return;
    }

    Modal.confirm({
      title: hasBankDetails ? "Update Bank Details?" : "Save Bank Details?",
      icon: <ExclamationCircleOutlined style={{ color: "#ff0055" }} />,
      content: (
        <div style={{ marginTop: 12 }}>
          <p style={{ marginBottom: 10 }}>Please verify before saving:</p>
          <div className="sb-confirm-box">
            <div className="sb-confirm-item">
              <BankOutlined /> <strong>Account:</strong> {maskAccount(values.bank_account)}
            </div>
            <div className="sb-confirm-item">
              <IdcardOutlined /> <strong>IFSC:</strong> {values.ifsc_code}
            </div>
            <div className="sb-confirm-item">
              <DollarOutlined /> <strong>UPI:</strong> {values.upi_id}
            </div>
          </div>
          <Alert
            message="Changes will be recorded for audit"
            type="warning"
            showIcon
            style={{ marginTop: 12, fontSize: 12 }}
          />
        </div>
      ),
      okText: hasBankDetails ? "Update" : "Save",
      okButtonProps: { className: "sb-modal-ok-btn" },
      onOk: async () => {
        try {
          setSaving(true);
          const updateData = {
            bank_account: values.bank_account.trim(),
            ifsc_code: values.ifsc_code.toUpperCase().trim(),
            upi_id: values.upi_id.toLowerCase().trim(),
          };
          await UserUpdate(staffId, updateData);
          message.success("✅ Bank details updated successfully!");
          setHasBankDetails(true);
          setLastUpdated(new Date().toISOString());
          await fetchBankDetails();
        } catch (error) {
          const errorMsg = error?.response?.data?.error || error?.message || "Failed to update";
          message.error(errorMsg);
        } finally {
          setSaving(false);
        }
      },
    });
  };

  return (
    <div className="sb-page">
      {/* ===== HEADER ===== */}
      <div className="sb-header">
        <div className="sb-header-left">
          <div className="sb-title-row">
            <div className="sb-title-icon">
              <BankOutlined />
            </div>
            <div className="sb-title-content">
              <h2 className="sb-title">Bank & Payment Details</h2>
              <p className="sb-subtitle">Manage your withdrawal account information securely</p>
            </div>
          </div>
        </div>
        <div className="sb-header-right">
          {hasBankDetails ? (
            <Badge 
              status="success" 
              text={
                <span className="sb-badge sb-badge-complete">
                  <CheckCircleOutlined /> Verified Account
                </span>
              }
            />
          ) : (
            <Badge 
              status="error" 
              text={
                <span className="sb-badge sb-badge-incomplete">
                  <ExclamationCircleOutlined /> Setup Required
                </span>
              }
            />
          )}
        </div>
      </div>

      {/* ===== ALERT BANNER ===== */}
      {!hasBankDetails && (
        <Alert
          message="Complete Your Bank Setup"
          description="Add your bank details to enable withdrawals from your commission earnings."
          type="warning"
          showIcon
          closable
          className="sb-alert-banner"
        />
      )}

      {/* ===== MAIN CONTENT ===== */}
      <Row gutter={[16, 16]}>
        
        {/* LEFT: FORM CARD */}
        <Col xs={24} lg={14}>
          <Card 
            className="sb-form-card"
            title={
              <div className="sb-form-header">
                <div className="sb-form-header-icon">
                  <EditOutlined />
                </div>
                <div>
                  <h3>{hasBankDetails ? "Update Bank Information" : "Add Bank Information"}</h3>
                  <p>All fields are required for withdrawal processing</p>
                </div>
              </div>
            }
            bordered={false}
          >
            <Form 
              form={form} 
              layout="vertical" 
              onFinish={handleSave}
              requiredMark="optional"
            >
              <Form.Item
                name="bank_account"
                label={
                  <span className="sb-form-label">
                    <BankOutlined /> Bank Account Number
                  </span>
                }
                rules={[
                  { required: true, message: "Bank account number is required" },
                  { pattern: /^[0-9]{9,18}$/, message: "Enter 9-18 digit account number" },
                ]}
                extra={
                  <span className="sb-form-extra">
                    <InfoCircleOutlined /> Enter your 9 to 18 digit bank account number
                  </span>
                }
              >
                <Input
                  placeholder="Enter your bank account number"
                  prefix={<BankOutlined />}
                  className="sb-input"
                  size="large"
                  maxLength={18}
                />
              </Form.Item>

              <Form.Item
                name="ifsc_code"
                label={
                  <span className="sb-form-label">
                    <IdcardOutlined /> IFSC Code
                  </span>
                }
                rules={[
                  { required: true, message: "IFSC code is required" },
                  { pattern: /^[A-Z]{4}0[A-Z0-9]{6}$/, message: "Format: SBIN0001234" },
                ]}
                extra={
                  <span className="sb-form-extra">
                    <InfoCircleOutlined /> Format: First 4 letters + 0 + 6 alphanumeric (e.g., SBIN0001234)
                  </span>
                }
              >
                <Input
                  placeholder="e.g., SBIN0001234"
                  prefix={<IdcardOutlined />}
                  className="sb-input"
                  size="large"
                  maxLength={11}
                  style={{ textTransform: "uppercase" }}
                  onChange={(e) => form.setFieldsValue({ ifsc_code: e.target.value.toUpperCase() })}
                />
              </Form.Item>

              <Form.Item
                name="upi_id"
                label={
                  <span className="sb-form-label">
                    <DollarOutlined /> UPI ID
                  </span>
                }
                rules={[
                  { required: true, message: "UPI ID is required" },
                  { pattern: /^[\w.-]+@[\w]+$/, message: "Format: name@paytm" },
                ]}
                extra={
                  <span className="sb-form-extra">
                    <InfoCircleOutlined /> Format: username@provider (e.g., yourname@paytm, name@oksbi)
                  </span>
                }
              >
                <Input
                  placeholder="e.g., yourname@paytm"
                  prefix={<DollarOutlined />}
                  className="sb-input"
                  size="large"
                />
              </Form.Item>

              <Divider style={{ margin: "16px 0" }} />

              <div className="sb-form-actions">
                <Button 
                  onClick={() => form.resetFields()} 
                  className="sb-reset-btn"
                  size="large"
                >
                  Reset
                </Button>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  icon={<SaveOutlined />}
                  loading={saving} 
                  className="sb-save-btn"
                  size="large"
                >
                  {hasBankDetails ? "Update Details" : "Save Details"}
                </Button>
              </div>
            </Form>
          </Card>
        </Col>

        {/* RIGHT: INFO PANEL */}
        <Col xs={24} lg={10}>
          <Space direction="vertical" size={14} style={{ width: "100%" }}>
            
            {/* Current Details */}
            {hasBankDetails && staffData && (
              <Card 
                className="sb-current-card"
                bordered={false}
                title={
                  <div className="sb-current-header">
                    <h3><LockOutlined /> Saved Details</h3>
                    <Button 
                      size="small"
                      className="sb-toggle-btn"
                      onClick={() => setShowAccount(!showAccount)}
                      icon={showAccount ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                    >
                      {showAccount ? "Hide" : "Show"}
                    </Button>
                  </div>
                }
              >
                <div className="sb-current-list">
                  <div className="sb-current-item">
                    <div className="sb-current-icon bank">
                      <BankOutlined />
                    </div>
                    <div className="sb-current-info">
                      <span className="sb-current-label">Bank Account</span>
                      <span className="sb-current-value">
                        {showAccount ? staffData.bank_account : maskAccount(staffData.bank_account)}
                      </span>
                    </div>
                    <Tooltip title="Copy">
                      <Button 
                        type="text" 
                        icon={<CopyOutlined />} 
                        size="small"
                        onClick={() => handleCopy(staffData.bank_account, "Account")}
                      />
                    </Tooltip>
                  </div>

                  <div className="sb-current-item">
                    <div className="sb-current-icon ifsc">
                      <IdcardOutlined />
                    </div>
                    <div className="sb-current-info">
                      <span className="sb-current-label">IFSC Code</span>
                      <span className="sb-current-value">{staffData.ifsc_code || "N/A"}</span>
                    </div>
                    <Tooltip title="Copy">
                      <Button 
                        type="text" 
                        icon={<CopyOutlined />} 
                        size="small"
                        onClick={() => handleCopy(staffData.ifsc_code, "IFSC")}
                      />
                    </Tooltip>
                  </div>

                  <div className="sb-current-item">
                    <div className="sb-current-icon upi">
                      <DollarOutlined />
                    </div>
                    <div className="sb-current-info">
                      <span className="sb-current-label">UPI ID</span>
                      <span className="sb-current-value">{staffData.upi_id || "N/A"}</span>
                    </div>
                    <Tooltip title="Copy">
                      <Button 
                        type="text" 
                        icon={<CopyOutlined />} 
                        size="small"
                        onClick={() => handleCopy(staffData.upi_id, "UPI")}
                      />
                    </Tooltip>
                  </div>
                </div>

                {lastUpdated && (
                  <div className="sb-last-updated">
                    <CalendarOutlined /> Last updated: {formatDateTime(lastUpdated)}
                  </div>
                )}
              </Card>
            )}

            {/* Security Info */}
            <Card className="sb-security-card" bordered={false}>
              <div className="sb-security-icon">
                <SecurityScanOutlined />
              </div>
              <h4>Security & Privacy</h4>
              <div className="sb-security-list">
                <div className="sb-security-item">
                  <SafetyOutlined /> <span>Account numbers are masked for security</span>
                </div>
                <div className="sb-security-item">
                  <LockOutlined /> <span>All changes recorded in audit log</span>
                </div>
                <div className="sb-security-item">
                  <CheckCircleOutlined /> <span>Required for withdrawal processing</span>
                </div>
                <div className="sb-security-item">
                  <SecurityScanOutlined /> <span>Data encrypted & protected</span>
                </div>
              </div>
            </Card>

            {/* Staff Info */}
            <Card className="sb-staff-info-card" bordered={false}>
              <h4><UserOutlined /> Staff Account</h4>
              <div className="sb-staff-info-list">
                <div className="sb-staff-info-row">
                  <span>Staff ID</span>
                  <Tag className="sb-info-tag">STAFF-{String(staffId).padStart(4, "0")}</Tag>
                </div>
                <div className="sb-staff-info-row">
                  <span>Account Status</span>
                  <Tag color="success" className="sb-info-tag">● Active</Tag>
                </div>
                <div className="sb-staff-info-row">
                  <span>Bank Status</span>
                  <Tag 
                    color={hasBankDetails ? "success" : "warning"} 
                    className="sb-info-tag"
                  >
                    {hasBankDetails ? "✅ Complete" : "⚠️ Pending"}
                  </Tag>
                </div>
              </div>
            </Card>
          </Space>
        </Col>
      </Row>
    </div>
  );
}

export default StaffBankDetails;